// Sugalarm — orientation read for the mobile variant (cycle 114).
//
// Returns true when the viewport is in landscape orientation, updating
// live on rotation (subscribes to the `(orientation: landscape)` media
// query) so a rotate re-renders without a reload.
//
// This is the SINGLE OWNER of the orientation read for *layout within*
// the mobile variant — mirroring the single-owner convention of
// `lib/deviceClass.ts` / `useFrameVariant`. No other mobile-layout code
// may call `matchMedia` for orientation directly.
//
// It deliberately does NOT touch `useFrameVariant`'s resolution: variant
// detection is unchanged, including the cycle-52 iPad step-0 logic (iPad
// landscape → kiosk) and the cycle-39 precedence. An iPhone in landscape
// still resolves to "mobile" (it never matches `isIPadClassRuntime()`);
// this hook only governs the landscape *layout* inside that mobile
// variant. `useFrameVariant` keeps its own (orientation: portrait)
// listener for the iPad kiosk↔mobile flip — the two are independent.

import { useEffect, useState } from "react";

const LANDSCAPE_QUERY = "(orientation: landscape)";

function compute(): boolean {
  if (typeof window === "undefined") return false;
  return window.matchMedia(LANDSCAPE_QUERY).matches;
}

export function useIsLandscape(): boolean {
  const [landscape, setLandscape] = useState<boolean>(compute);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia(LANDSCAPE_QUERY);
    const onChange = () => setLandscape(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  return landscape;
}
